// TechnicalErrorFormModal.vue
<template>
  <b-modal
    ref="modal"
    static
    :visible="show"
    :title="modalTitle"
    header-bg-variant="light"
    @hidden="resetForm"
    @close="close"
    @cancel="close"
    @ok.prevent="handleSubmit"
    :ok-disabled="isSubmitting"
    no-close-on-backdrop
    centered
  >
    <b-form @submit.prevent="handleSubmit" ref="form">
      <b-form-group
        label="Nhóm lỗi:"
        label-for="error-group-input"
        :state="isValidErrorGroup"
        :invalid-feedback="errorGroupInvalidFeedback"
      >
        <b-form-select
          id="error-group-input"
          v-model="form.errorGroupId"
          :options="errorGroupOptions"
          :state="isValidErrorGroup"
          required
          :disabled="isSubmitting || isEdit"
          @change="handleErrorGroupChange"
        ></b-form-select>
        <small class="form-text text-muted" v-if="isEdit">
          Không thể thay đổi nhóm lỗi sau khi đã tạo
        </small>
      </b-form-group>

      <b-form-group
        label="Mã lỗi kỹ thuật:"
        label-for="code-input"
        :state="isValidCode"
        :invalid-feedback="codeInvalidFeedback"
      >
        <b-input-group>
          <b-input-group-prepend v-if="selectedErrorGroupCode">
            <b-input-group-text>{{ selectedErrorGroupCode }}-</b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            id="code-input"
            v-model="form.codeAfterPrefix"
            :state="isValidCode"
            required
            placeholder="Nhập mã lỗi kỹ thuật"
            maxlength="50"
            :disabled="isSubmitting || !form.errorGroupId"
          ></b-form-input>
        </b-input-group>
        <small class="form-text text-muted">
          Mã lỗi sẽ được tự động ghép với mã nhóm lỗi
        </small>
      </b-form-group>

      <b-form-group
        label="Tên lỗi kỹ thuật:"
        label-for="name-input"
        :state="isValidName"
        :invalid-feedback="nameInvalidFeedback"
      >
        <b-form-input
          id="name-input"
          v-model="form.name"
          :state="isValidName"
          required
          placeholder="Nhập tên lỗi kỹ thuật"
          maxlength="255"
          :disabled="isSubmitting"
        ></b-form-input>
      </b-form-group>

      <b-form-group label-for="active-input">
        <b-form-checkbox
          id="active-input"
          v-model="form.isActive"
          :disabled="isSubmitting"
          switch
        >
          {{ form.isActive ? 'Hoạt động' : 'Không hoạt động' }}
        </b-form-checkbox>
      </b-form-group>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel()">
        Hủy bỏ
      </b-button>
      <b-button
        variant="primary"
        :disabled="isSubmitting || !isFormValid"
        @click="handleSubmit"
      >
        <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
        {{ isEdit ? 'Cập nhật' : 'Thêm mới' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { TechnicalErrorService } from './service/technical-error.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'TechnicalErrorFormModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    technicalError: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    preSelectedErrorGroupId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isSubmitting: false,
      errorGroupOptions: [
        { value: null, text: 'Chọn nhóm lỗi' }
      ],
      errorGroups: [],
      form: {
        errorGroupId: null,
        code: '',
        codeAfterPrefix: '',
        name: '',
        isActive: true,
      },
      validationErrors: {
        errorGroupId: '',
        code: '',
        name: '',
      },
    };
  },
  computed: {
    modalTitle() {
      return this.isEdit
        ? `Chỉnh sửa lỗi kỹ thuật: ${this.technicalError?.name || ''}`
        : 'Thêm mới lỗi kỹ thuật';
    },
    isValidErrorGroup() {
      return this.validationErrors.errorGroupId ? false : null;
    },
    isValidCode() {
      return this.validationErrors.code ? false : null;
    },
    isValidName() {
      return this.validationErrors.name ? false : null;
    },
    errorGroupInvalidFeedback() {
      return this.validationErrors.errorGroupId;
    },
    codeInvalidFeedback() {
      return this.validationErrors.code;
    },
    nameInvalidFeedback() {
      return this.validationErrors.name;
    },
    isFormValid() {
      return !!this.form.errorGroupId && 
        !!this.form.codeAfterPrefix && 
        !!this.form.name;
    },
    selectedErrorGroupCode() {
      if (!this.form.errorGroupId) return '';
      const group = this.errorGroups.find(g => g.id === this.form.errorGroupId);
      return group ? group.code : '';
    },
  },
  watch: {
    technicalError(newVal) {
      if (newVal) {
        this.initForm();
      }
    },
    show(newVal) {
      if (newVal) {
        this.fetchErrorGroups();
      }
    },
    preSelectedErrorGroupId(newVal) {
      if (newVal && !this.isEdit) {
        this.form.errorGroupId = newVal;
      }
    }
  },
  methods: {
    async fetchErrorGroups() {
      try {
        const response = await TechnicalErrorService.getErrorGroups();
        if (response.data.status === 200) {
          this.errorGroups = response.data.data.items || [];
          this.errorGroupOptions = [
            { value: null, text: 'Chọn nhóm lỗi' },
            ...this.errorGroups.map(group => ({
              value: group.id,
              text: `${group.code} - ${group.name}`
            }))
          ];
          
          // If preSelectedErrorGroupId is provided and we're not in edit mode
          if (this.preSelectedErrorGroupId && !this.isEdit) {
            this.form.errorGroupId = this.preSelectedErrorGroupId;
          }
        }
      } catch (error) {
        console.error('Error fetching error groups:', error);
        makeToastFaile('Không thể tải danh sách nhóm lỗi');
      }
    },
    
    handleErrorGroupChange() {
      // Reset code part when error group changes
      this.form.codeAfterPrefix = '';
    },
    
    initForm() {
      if (this.technicalError) {
        // Split the code to get the part after the group prefix
        let codeAfterPrefix = '';
        if (this.technicalError.code && this.technicalError.errorGroup) {
          const prefix = this.technicalError.errorGroup.code + '-';
          codeAfterPrefix = this.technicalError.code.startsWith(prefix) 
            ? this.technicalError.code.substring(prefix.length)
            : this.technicalError.code;
        }
        
        this.form = {
          ...this.form,
          errorGroupId: this.technicalError.errorGroupId,
          code: this.technicalError.code,
          codeAfterPrefix: codeAfterPrefix,
          name: this.technicalError.name,
          isActive: this.technicalError.isActive
        };
      }
    },
    
    resetForm() {
      this.form = {
        errorGroupId: this.preSelectedErrorGroupId || null,
        code: '',
        codeAfterPrefix: '',
        name: '',
        isActive: true,
      };
      this.validationErrors = {
        errorGroupId: '',
        code: '',
        name: '',
      };
    },
    
    validateForm() {
      this.validationErrors = {
        errorGroupId: '',
        code: '',
        name: '',
      };

      if (!this.form.errorGroupId) {
        this.validationErrors.errorGroupId = 'Vui lòng chọn nhóm lỗi';
      }

      if (!this.form.codeAfterPrefix.trim()) {
        this.validationErrors.code = 'Mã lỗi kỹ thuật không được để trống';
      }

      if (!this.form.name.trim()) {
        this.validationErrors.name = 'Tên lỗi kỹ thuật không được để trống';
      }

      return !this.validationErrors.errorGroupId && 
        !this.validationErrors.code && 
        !this.validationErrors.name;
    },
    
    prepareFullCode() {
      if (!this.form.errorGroupId || !this.form.codeAfterPrefix) {
        return '';
      }
      
      const group = this.errorGroups.find(g => g.id === this.form.errorGroupId);
      if (!group) return this.form.codeAfterPrefix;
      
      return `${group.code}-${this.form.codeAfterPrefix.trim()}`;
    },
    
    handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      const fullCode = this.prepareFullCode();
      
      const payload = {
        errorGroupId: this.form.errorGroupId,
        code: fullCode,
        name: this.form.name.trim(),
        isActive: this.form.isActive,
      };

      const request = this.isEdit
        ? TechnicalErrorService.update(this.technicalError.id, payload)
        : TechnicalErrorService.create(payload);

      request
        .then((response) => {
          if (response.data.status === 200) {
            const message = this.isEdit
              ? 'Cập nhật lỗi kỹ thuật thành công!'
              : 'Thêm mới lỗi kỹ thuật thành công!';

            makeToastSuccess(message);
            this.$emit('technical-error-saved', response.data.data);
          } else {
            makeToastFaile(
              response.data.message || 'Có lỗi xảy ra, vui lòng thử lại sau!',
            );
          }
        })
        .catch((error) => {
          console.error('Error saving technical error:', error);

          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((key) => {
              if (key in this.validationErrors) {
                this.validationErrors[key] = errors[key];
              }
            });
          } else {
            makeToastFaile(
              error.message || 'Có lỗi xảy ra, vui lòng thử lại sau!',
            );
          }
        })
        .finally(() => {
          this.isSubmitting = false;
          if (!Object.values(this.validationErrors).some((v) => v)) {
            this.close();
          }
        });
    },
    
    close() {
      this.$emit('close');
    },
  },
  created() {
    if (this.show) {
      this.fetchErrorGroups();
    }
  }
};
</script>